import React, { useEffect, useRef, useState } from 'react';
import { Icon, IconsEnum } from 'components/Icons';

import { SkeletonArea } from 'components/skeletonArea';
import { SportMarcatoriCarouselCard } from 'features/sport/components/sportMarcatoriCarousel/sportMarcatoriCarouselCard';
import classnames from 'classnames';
import { isTruthy } from 'utility/functions';
import { rootItems } from 'features/live/liveNav/utility';
import { selectIsLoading } from 'features/location/selectors';
import styles from './SportMarcatoriCarousel.module.scss';
import { useAvvenimentoList } from 'features/sport/hooks';
import { useTypedSelector } from 'lib/centralStore';
import useWindowSize from 'hooks/useWindowSize';

export type SportMarcatoriCarouselProps = {
  avvenimentoKeys: Array<string>;
  hasVerticalMargin?: boolean;
  activeCompetitor?: string;
};

export const SportMarcatoriCarousel = ({
  hasVerticalMargin,
  avvenimentoKeys,
  activeCompetitor,
}: SportMarcatoriCarouselProps) => {
  const [isAtEnd, setIsAtEnd] = useState(false);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isScrollable, setIsScrollable] = useState<boolean>(false);

  const isPageLoading = useTypedSelector(selectIsLoading);
  const { isMobile } = useWindowSize();
  const avvenimenti = useAvvenimentoList(avvenimentoKeys, false);

  const containerRef = useRef<HTMLUListElement>(null);
  const toScroll = useRef(true);

  const handleScroll = () => {
    if (containerRef.current) {
      const currentPosition = containerRef.current.scrollLeft;
      const isStart = currentPosition === 0;
      const isEnd = currentPosition === containerRef.current.scrollWidth - containerRef.current.clientWidth;

      setIsAtStart(isStart);
      setIsAtEnd(isEnd);
    }
  };

  const goBack = (refElement: HTMLUListElement | null) => {
    const delta = refElement?.offsetWidth;

    if (refElement && delta) {
      refElement.scrollLeft -= delta - 250;
    }
  };

  const goOn = (refElement: HTMLUListElement | null) => {
    const delta = refElement?.offsetWidth;

    if (refElement && delta) {
      refElement.scrollLeft += delta - 250;
    }
  };

  useEffect(() => {
    if (isPageLoading) {
      toScroll.current = true;
    }
  }, [isPageLoading]);

  useEffect(() => {
    if (!containerRef.current) return;

    const listItems = containerRef.current.querySelectorAll('li');
    if (listItems.length <= rootItems.length) return;

    let scrollLeft = 0;
    let listItemsWidth: number = 0;
    const containerWidth = containerRef.current.offsetWidth;

    listItems.forEach((item) => {
      const isSelected = item.getAttribute('data-selected') ?? undefined;
      if (isTruthy(isSelected)) {
        scrollLeft = listItemsWidth;
      }
      listItemsWidth += item.getBoundingClientRect().width + 8;
    });

    if (containerWidth && listItemsWidth < containerWidth) {
      setIsScrollable(false);
    } else {
      setIsScrollable(true);
    }

    if (toScroll.current && !isPageLoading) {
      toScroll.current = false;
      const half = containerWidth * 0.5;
      if (scrollLeft > half) {
        containerRef.current.scrollLeft = scrollLeft - half;
      } else {
        containerRef.current.scrollLeft = 0;
      }
    }

    handleScroll();
  }, [isPageLoading, avvenimenti]);

  return (
    <div className={styles.container}>
      {!isMobile && isScrollable && !isAtStart && (
        <button className={classnames(styles.button, styles.buttonLeft)} onClick={() => goBack(containerRef.current)}>
          <Icon iconId={IconsEnum.CHEVRON_LEFT} className={styles.icon} color="var(--color-on-bg-primary)" />
        </button>
      )}
      <ul
        className={classnames(styles.list, { [styles.verticalMargin]: hasVerticalMargin })}
        ref={containerRef}
        onScroll={handleScroll}
      >
        {avvenimenti.map((avvenimento, index) => {
          const { key } = avvenimento;
          let competitorEnabled: 'both' | 1 | 2 = 'both';
          if (activeCompetitor) {
            const { firstCompetitor } = avvenimento;
            competitorEnabled = activeCompetitor.toUpperCase() === firstCompetitor?.descrizione?.toUpperCase() ? 1 : 2;
          }

          return (
            <li key={index}>
              <SportMarcatoriCarouselCard avvenimentoKey={key!} competitorEnabled={competitorEnabled} />
            </li>
          );
        })}
      </ul>
      {!isMobile && isScrollable && !isAtEnd && (
        <button className={classnames(styles.button, styles.buttonRight)} onClick={() => goOn(containerRef.current)}>
          <Icon iconId={IconsEnum.CHEVRON_RIGHT} className={styles.icon} color="var(--color-on-bg-primary)" />
        </button>
      )}
    </div>
  );
};

export const MarcatoriCarouselSkeleton = () => {
  return (
    <div className={styles.skeletonCarouselContainer}>
      {Array(10)
        .fill(null)
        .map((_, index) => (
          <div className={styles.skeletonButtonsCarouselContainer} key={`carousel_${index}`}>
            <SkeletonArea className={styles.skeletonCarouselText} />
            <SkeletonArea className={styles.skeletonButtonCarousel} />
          </div>
        ))}
    </div>
  );
};